import React from 'react';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import {USER_CONTENT_RICH_CLASS} from '../../utils/userContentSanitize';
import useTranslateDescription from '../../hooks/useTranslateDescription';
import styles from './TranslateControl.module.scss';

const SPRING = {type: 'spring', duration: 0.3, bounce: 0};
const EXIT_SPRING = {type: 'spring', duration: 0.18, bounce: 0};

/**
 * Twitter-style Translate / See original control for an About this song body.
 * Renders the description paragraph and, when the guessed language differs
 * from the reader, a text button underneath.
 */
const TranslateControl = ({
  songId,
  description,
  mentionedHandles,
  textClassName,
  renderDescriptionWithLinks,
  onMentionClick,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const {
    offer,
    shown,
    loading,
    error,
    translatedText,
    sourceName,
    targetLang,
    translate,
    showOriginal,
  } = useTranslateDescription(songId, description);

  const originalHtml = renderDescriptionWithLinks(
    description,
    mentionedHandles
  );
  const translatedHtml = shown
    ? renderDescriptionWithLinks(translatedText, mentionedHandles)
    : '';

  const handleClick = (event) => {
    if (onMentionClick) onMentionClick(event);
  };

  const spring = prefersReducedMotion ? {duration: 0} : SPRING;
  const exitSpring = prefersReducedMotion ? {duration: 0} : EXIT_SPRING;
  const blur = prefersReducedMotion ? 'blur(0px)' : 'blur(4px)';
  const bodyClass = `${textClassName} ${USER_CONTENT_RICH_CLASS} ${styles.body}`.trim();

  return (
    <>
      <motion.div className={styles.swap} layout="size" transition={spring}>
        <AnimatePresence initial={false}>
          {shown ? (
            <motion.div
              key="translated"
              className={`${bodyClass} ${styles.layer}`}
              lang={targetLang}
              dir="auto"
              dangerouslySetInnerHTML={{__html: translatedHtml}}
              onClick={handleClick}
              initial={{opacity: 0, y: 8, filter: blur}}
              animate={{opacity: 1, y: 0, filter: 'blur(0px)'}}
              exit={{
                opacity: 0,
                y: 8,
                filter: blur,
                transition: exitSpring,
              }}
              transition={spring}
            />
          ) : (
            <motion.div
              key="original"
              className={`${bodyClass} ${styles.layer}`}
              dir="auto"
              dangerouslySetInnerHTML={{__html: originalHtml}}
              onClick={handleClick}
              initial={{opacity: 0, y: -8, filter: blur}}
              animate={{opacity: 1, y: 0, filter: 'blur(0px)'}}
              exit={{
                opacity: 0,
                y: -8,
                filter: blur,
                transition: exitSpring,
              }}
              transition={spring}
            />
          )}
        </AnimatePresence>
      </motion.div>
      {offer && (
        <div className={styles.bar}>
          <AnimatePresence mode="wait" initial={false}>
            {shown ? (
              <motion.div
                key="see-original"
                className={styles.barInner}
                initial={{opacity: 0, y: 4, filter: blur}}
                animate={{opacity: 1, y: 0, filter: 'blur(0px)'}}
                exit={{opacity: 0, y: -4, filter: blur, transition: exitSpring}}
                transition={{...spring, delay: prefersReducedMotion ? 0 : 0.08}}
              >
                <button
                  type="button"
                  className={styles.toggle}
                  onClick={showOriginal}
                  aria-label="See original description"
                >
                  See original
                </button>
                {sourceName ? (
                  <span className={styles.credit}>
                    Translated from {sourceName}
                  </span>
                ) : null}
              </motion.div>
            ) : (
              <motion.div
                key="translate"
                className={styles.barInner}
                initial={{opacity: 0, y: -4, filter: blur}}
                animate={{opacity: 1, y: 0, filter: 'blur(0px)'}}
                exit={{opacity: 0, y: 4, filter: blur, transition: exitSpring}}
                transition={spring}
              >
                <button
                  type="button"
                  className={styles.toggle}
                  onClick={translate}
                  disabled={loading}
                  aria-busy={loading}
                  aria-label="Translate this description"
                >
                  {loading ? 'Translating…' : 'Translate'}
                </button>
                {error ? (
                  <span className={styles.error} role="alert">
                    Couldn&apos;t translate. Try again.
                  </span>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );
};

export default TranslateControl;
